import {
  DeviceType,
  FirmwareKind,
  deviceTypesWithAccurateBatteryPercentage,
  firmwareTypeForDevice,
  getIBeaconVendor,
  getSrdBeaconId,
  lastSeenThresholdFor,
} from '@ixicare/resources';
import { formatDate, Link, OfflineDevice } from '@ixicare/ui';
import BatteryIndicator from '~components/BatteryIndicator';
import { t } from '~i18n';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { deviceStateLabel } from '~modules/devices/utils/deviceState';
import { SoftwareVersionCell } from '~modules/firmware/components/SoftwareVersionCell';
import { OtaTasksByKind } from '~modules/firmware/hooks/useOtaTasks';

export type TransformContext = {
  isArchived?: boolean;
  siteId?: number;
  /** Site of the device, so the owner link switches to it when it differs from the selected site. */
  siteUuid?: string;
  showTypeColumn?: boolean;
  /** Open or failed updates, shown next to the software or firmware version they replace. */
  otaTasksByKind?: OtaTasksByKind;
  /** Makes a version open the device's update details of that kind, for devices that take OTA updates. */
  onOpenFirmware?: (device: any, kind: FirmwareKind) => void;
};

function ownerCell(owner: any, siteUuid?: string) {
  if (!owner) return undefined;
  const name = `${owner.firstName} ${owner.lastName}`;
  if (!owner.id) return name;
  return <Link href={`/residents/${owner.id}${siteUuid ? `?site=${siteUuid}` : ''}`}>{name}</Link>;
}

function softwareVersionCell(
  device: any,
  version: string | undefined,
  context: TransformContext,
  kind: FirmwareKind = 'software'
) {
  const { onOpenFirmware } = context;
  if (!onOpenFirmware || !firmwareTypeForDevice(device.deviceType, kind)) return version;
  return (
    <SoftwareVersionCell
      version={version}
      kind={kind}
      task={context.otaTasksByKind?.[kind].get(device.id)}
      onOpen={() => onOpenFirmware(device, kind)}
    />
  );
}

function lastSeenCell(date: any, threshold: Date = lastSeenThresholdFor(DeviceType.GATEWAY)) {
  if (!date || !formatDate(date)) return undefined;
  return new Date(date) >= threshold ? formatDate(date) : <OfflineDevice date={date} />;
}

export const BUTTON_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.emergencyButton'),
  t('devices.table.installationZone'),
  t('devices.table.description'),
  t('devices.table.softwareVersion'),
  t('devices.table.batteryLevel'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const BUTTON_COLUMN_IDS = [
  'mac_address',
  'emergency_button',
  'room_number',
  'description',
  'sw_version',
  'battery_level',
  'updated_on',
  'state',
];

export const API_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.apiIdentifier'),
  t('devices.table.installationZone'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const API_COLUMN_IDS = ['mac_address', 'description', 'api_identifier', 'room_number', 'updated_on', 'state'];

export const WEARABLE_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.owner'),
  t('devices.table.deviceType'),
  t('devices.table.description'),
  t('global.phoneNumber'),
  t('devices.table.softwareVersion'),
  t('devices.table.batteryLevel'),
  t('devices.table.imeiNumber'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const WEARABLE_COLUMN_IDS = [
  'mac_address',
  'owner',
  'device_type',
  'description',
  'phone_number',
  'sw_version',
  'battery_level',
  'imei_number',
  'updated_on',
  'state',
];

// Wristwatches (the ixi Pro) also report the firmware image under their software.
const withFirmwareVersion = <T,>(columns: T[], firmwareColumn: T) => {
  const index = WEARABLE_COLUMN_IDS.indexOf('sw_version') + 1;
  return [...columns.slice(0, index), firmwareColumn, ...columns.slice(index)];
};

export const WRISTWATCH_COLUMNS = withFirmwareVersion(WEARABLE_COLUMNS, t('devices.table.firmwareVersion'));

export const WRISTWATCH_COLUMN_IDS = withFirmwareVersion(WEARABLE_COLUMN_IDS, 'fw_version');

export const GATEWAY_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.generatedOn'),
  t('devices.table.installationZone'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const GATEWAY_COLUMN_IDS = ['mac_address', 'description', 'created', 'installation_zone', 'last_seen', 'state'];

export const BEACON_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.generatedOn'),
  t('devices.table.batteryLevel'),
  t('devices.table.installationZone'),
  t('devices.table.calibrationFactor'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const BEACON_COLUMN_IDS = [
  'mac_address',
  'description',
  'created',
  'battery_level',
  'installation_zone',
  'calibration_factor',
  'last_seen',
  'state',
];

export function transformButtonToRow(device: any, context: TransformContext) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Button');
  }

  row.push(
    device.macAddress,
    device.subType != null ? 'Active' : 'Inactive',
    device.roomNumber,
    device.description,
    softwareVersionCell(device, device.swVersion, context),
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    lastSeenCell(device.status?.updatedOn),
    deviceStateLabel(device.billingState, isArchived)
  );

  return row;
}

export function transformWristwatchToRow(device: any, context: TransformContext) {
  return transformWearableToRow(device, context, true);
}

export function transformWearableToRow(device: any, context: TransformContext, showFirmwareVersion = false) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Wearable');
  }

  row.push(
    device.macAddress,
    ownerCell(device.owner, context.siteUuid),
    device.deviceType && t(`device.types.${device.deviceType.toLowerCase()}`),
    device.description,
    device.phoneNumber,
    softwareVersionCell(device, device.swVersion?.match(/\d+(\.\d+)+/)?.[0], context),
    ...(showFirmwareVersion ? [softwareVersionCell(device, device.fwVersion, context, 'firmware')] : []),
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    device.imeiNumber,
    lastSeenCell(device.status?.updatedOn),
    deviceStateLabel(device.billingState, isArchived)
  );

  return row;
}

export const EVIEW_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.owner'),
  t('devices.table.description'),
  t('global.phoneNumber'),
  t('devices.table.batteryLevel'),
  t('devices.table.imeiNumber'),
  t('devices.table.lastSeen'),
  t('devices.table.state'),
];

export const EVIEW_COLUMN_IDS = [
  'mac_address',
  'owner',
  'description',
  'phone_number',
  'battery_level',
  'imei_number',
  'updated_on',
  'state',
];

export function transformEviewToRow(device: any, context: TransformContext) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(device.deviceType === DeviceType.TWIG ? 'Twig' : 'Eview');
  }

  row.push(
    device.macAddress,
    ownerCell(device.owner, context.siteUuid),
    device.description,
    device.phoneNumber,
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    device.imeiNumber,
    lastSeenCell(device.status?.updatedOn),
    deviceStateLabel(device.billingState, isArchived)
  );

  return row;
}

// API_EXTERNAL devices are pre-registered through the public API and identified by
// their `apiIdentifier` (camelized from the backend's `api_identifier`) rather than IMEI/phone.
export function transformApiToRow(device: any, context: TransformContext) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(t('device.types.api_external'));
  }

  row.push(
    device.macAddress,
    device.description,
    device.apiIdentifier,
    device.roomNumber,
    lastSeenCell(device.status?.updatedOn),
    deviceStateLabel(device.billingState, isArchived)
  );

  return row;
}

export function transformGatewayToRow(gateway: any, context: TransformContext) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Gateway');
  }

  row.push(
    gateway.macAddress,
    gateway.description,
    gateway.created && formatDate(gateway.created),
    gateway.zone?.name,
    lastSeenCell(gateway.lastSeen),
    deviceStateLabel(gateway.billingState, isArchived)
  );

  return row;
}

/** What the beacon is known by: its address, or what an iBeacon advertises — the address such a
 *  beacon is stored under is one we made up for it, so it means nothing to whoever installed it. */
function beaconIdentity(beacon: any): string {
  if (beacon.ibeaconUuid) {
    const vendor = getIBeaconVendor(beacon.ibeaconUuid)?.name ?? t('device.types.beacon');
    return `${vendor} ${beacon.ibeaconMajor}/${beacon.ibeaconMinor}`;
  }
  const srdBeaconId = getSrdBeaconId(beacon.macAddress);
  if (srdBeaconId !== undefined) return `${t('device.form.beaconSourceSrd')} ${srdBeaconId}`;
  return beacon.macAddress;
}

export function transformBeaconToRow(beacon: any, context: TransformContext) {
  const { isArchived = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Beacon');
  }

  row.push(
    beaconIdentity(beacon),
    beacon.description,
    beacon.created ? formatDate(beacon.created) : undefined,
    typeof beacon.batteryLevel === 'number' ? (
      <BatteryIndicator inaccurate percentage={beacon.batteryLevel as number} />
    ) : undefined,
    beacon.zone?.name,
    beacon.calibrationFactor === 2 || beacon.calibrationFactor === 0.0 ? '0' : beacon.calibrationFactor,
    lastSeenCell(beacon.lastSeen, lastSeenThresholdFor(DeviceType.BEACON)),
    deviceStateLabel(beacon.billingState, isArchived)
  );

  return row;
}

export const DBG_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.deviceType'),
  t('devices.table.description'),
];

export const DBG_COLUMN_IDS = ['mac_address', 'device_type', 'description'];

export function transformDBGToRow(dbg: DBGWithType, context: TransformContext) {
  const { showTypeColumn = true } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(dbg.type);
  }

  row.push(dbg.macAddress, dbg.type, dbg.description);

  return row;
}
